import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import config from '../config/config';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import {
  Heart, Share2, Star, Truck, RotateCcw, Shield,
  Plus, Minus, Check, X, ChevronLeft, ChevronRight,
  ThumbsUp, Filter
} from 'lucide-react';
import FlashBar from "../components/FlashBar";
import ShareableNav from "../components/ShareableNav";
import Suggestions from "../components/Suggestions";
import CategoriesNav from "../components/CategoriesNav";
import NavigationBar from "../components/NavigationBar";
import FooterSection from "../components/FooterSection";
import CartSection from "../components/CartSection";
import SearchWidget from "../components/SearchWidget";
import BulkTable from "../pages/BulkTable";
import { useCart } from '../context/CartContext';

// ── Cache Config ─────────────────────────────────────────────────────────────
const CACHE_KEY_LAYOUT = 'layout_settings__cache';
const CACHE_KEY_PRODUCT = (id) => `product_viewsz_cache_${id}`;
const CACHE_TTL = 24 * 60 * 60 * 1000 * 5; // 5 days

const getCached = (key) => {
  try {
    const item = localStorage.getItem(key);
    if (!item) return null;
    const { data, ts } = JSON.parse(item);
    if (Date.now() - ts < CACHE_TTL) return data;
    localStorage.removeItem(key);
    return null;
  } catch {
    localStorage.removeItem(key);
    return null;
  }
};

const setCached = (key, data) => {
  localStorage.setItem(key, JSON.stringify({ data, ts: Date.now() }));
};

// ── Skeleton ─────────────────────────────────────────────────────────────────
const ProductSkeleton = () => (
  <div className="min-h-screen bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 md:py-12">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12 animate-pulse">
        <div className="lg:col-span-2 space-y-6">
          <div className="aspect-[4/5] md:aspect-[5/6] bg-gray-200 rounded-2xl" />
          <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="aspect-square bg-gray-200 rounded-xl" />
            ))}
          </div>
        </div>
        <div className="lg:sticky lg:top-8">
          <div className="bg-white rounded-2xl p-6 md:p-8 border border-gray-100 space-y-6">
            <div className="h-10 bg-gray-200 rounded w-3/4" />
            <div className="h-6 bg-gray-200 rounded w-1/2" />
            <div className="h-12 bg-gray-200 rounded w-1/3" />
            <div className="h-14 bg-gray-200 rounded" />
            <div className="h-14 bg-gray-200 rounded" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

const ReviewsSection = ({ rating = 0.0, reviewCount = 0, stats = {}, sampleReviews = [], navigate, id, featuredBg }) => {
  const [selectedFilter, setSelectedFilter] = useState('All');

  const hasReviews = (stats?.total || reviewCount || 0) > 0 && sampleReviews?.length > 0;

  const renderStars = (stars, size = "w-5 h-5") => (
    <div className="flex gap-0.5">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          className={`${size} ${i < Math.floor(stars) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
        />
      ))}
    </div>
  );

  if (sampleReviews?.length < 1) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 border-t border-gray-200">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 text-center md:text-left">
          Customer Reviews
        </h2>

        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-10 md:p-16 text-center">
          <div className="mx-auto w-24 h-24 bg-indigo-50 rounded-full flex items-center justify-center mb-8">
            <Star className="w-12 h-12 text-green-600" /><Star className="w-12 h-12 text-green-600" /><Star className="w-12 h-12 text-green-600" /><Star className="w-12 h-12 text-green-600" /><Star className="w-12 h-12 text-gray-200" />
          </div>

          <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-5">
            Be the first to write a review!
          </h3>

          <button
            onClick={() => navigate('/myAccount/rating/' + id)}
            className="inline-flex items-center gap-3 text-white px-12 py-5 rounded-xl font-semibold text-lg transition shadow-lg hover:shadow-xl transform hover:-translate-y-1 active:translate-y-0"
            style={{ backgroundColor: featuredBg }}
          >
            Write a Review
            <Star className="w-6 h-6" />
          </button>

          <p className="mt-10 text-sm text-gray-500">
            All reviews are moderated for authenticity and quality.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mt-[-15px] md:mt-0 mx-auto px-4 sm:px-6 py-3 border-t border-gray-200">
      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 text-center md:text-left">
        Customer Reviews
      </h2>

      <div className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl p-7 shadow-lg border border-gray-100 sticky top-8">
            <div className="text-center mb-7 pb-7 border-b border-gray-200">
              <div className="text-7xl font-black text-gray-900 mb-2">
                {stats?.average?.toFixed(1) ?? '—'}
              </div>
              <div className="flex justify-center mb-3">
                {renderStars(5, "w-6 h-6")}
              </div>
            </div>

            <div className="space-y-4 mb-8">
              {stats?.distribution?.length > 0 ? (
                stats.distribution.map((item) => (
                  <div key={item.stars} className="flex items-center gap-4">
                    <span className="text-base font-semibold text-gray-700 w-7 text-right">
                      {item.stars}★
                    </span>
                    <div className="flex-1 bg-gray-200 rounded-full h-2.5 overflow-hidden">
                      <div
                        className="bg-yellow-400 h-full rounded-full transition-all duration-500"
                        style={{ width: `${item.percentage ?? 0}%` }}
                      />
                    </div>
                    <span className="text-sm text-gray-600 w-14 text-right font-medium">
                      {item.percentage ?? 0}%
                    </span>
                  </div>
                ))
              ) : (
                <div className="text-gray-500 text-center py-4 italic">
                  Detailed rating breakdown not available yet
                </div>
              )}
            </div>

            <button onClick={() => navigate('/myAccount/rating/' + id)} className="w-full bg-gray-900 text-white py-4 rounded-xl font-semibold hover:bg-gray-800 transition text-lg shadow-sm hover:shadow">
              Write a Review
            </button>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-8">
          {sampleReviews.map((review) => (
            <div
              key={review.id}
              className="bg-white rounded-2xl p-7 shadow border border-gray-100 hover:shadow-xl transition-all duration-200"
            >
              <div className="flex items-start gap-5 mb-5">
                <div className="flex-1">
                  <div className="flex items-center flex-wrap gap-3 mb-2">
                    <h4 className="font-bold text-gray-900 text-lg">{review.name}</h4>
                    {review.verified && (
                      <div className="flex items-center gap-1.5 bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-medium">
                        <Check className="w-4 h-4" /> Verified Purchase
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-4 mb-4">
                    {renderStars(review.rating ?? 0, "w-5 h-5")}
                    <span className="text-gray-500 text-sm">• {review.date}</span>
                  </div>
                </div>
              </div>

              <h5 className="font-semibold text-gray-900 text-xl mb-3">
                {review.title || "Great experience"}
              </h5>

              <p className="text-gray-700 leading-relaxed mb-6">{review.content}</p>

              <div className="flex items-center gap-6 pt-5 border-t border-gray-100">
              </div>
            </div>
          ))}

          <div className="text-center pt-8">
            {sampleReviews?.length > 5 && (
              <button className="text-indigo-600 font-semibold hover:text-indigo-800 transition text-lg">
                Load more reviews →
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Stable Quantity + Add Button Component ──────────────────────────────────
const QuantityAndAddButton = React.memo(({
  allVariants,
  openVariants,
  featuredBg,
  activeBg,
  draftQty,
  setDraftQty,
  commitQuantity,
  addToCart,
  product,
  savingLoader,
  setSavingLoader,
}) => {
   if (product.qty < 1) {
    return (
        <button
        onClick={() => {
         alert('Product is out of stock. Check later');
        }}
      
        style={{
          padding: '1rem 0',
          borderRadius: '0.75rem',
          fontWeight: '700',
          color: 'white',
          minWidth: '180px',
          transition: 'all 0.2s ease',
          backgroundColor: savingLoader ? activeBg : featuredBg,
          opacity: savingLoader || draftQty < 1 ? 0.7 : 1,
          cursor: savingLoader || draftQty < 1 ? 'not-allowed' : 'pointer',
        }}
        className="flex-1 text-center shadow-md hover:shadow-lg active:scale-[0.98] transition-transform"
      >
        Out Of Stock
      </button>
    );
  }
  if (allVariants.length > 0) {
    return (
      <button
        onClick={openVariants}
        style={{
          padding: '1rem 0',
          borderRadius: '0.75rem',
          fontWeight: '700',
          color: 'white',
          minWidth: '180px',
          backgroundColor: featuredBg,
          cursor: 'pointer',
        }}
        className="flex-1 text-center"
      >
        Select Available Variants
      </button>
    );
  }



  return (
    <div className="bg-white px-4 py-5 shadow-xl flex flex-row gap-4 mt-6 sm:mt-10 z-30 rounded-xl">
    {product?.qty>0 && (
    <>
      <div className="flex items-center border-2 border-gray-300 rounded-lg overflow-hidden flex-shrink-0">
        <button
          type="button"
          onClick={() => setDraftQty(prev => Math.max(1, prev - 1))}
          className="w-12 h-12 flex items-center justify-center hover:bg-gray-100 active:bg-gray-200 transition"
          disabled={draftQty <= 1}
        >
          <Minus className="w-5 h-5" />
        </button>

        <input
          type="number"
          value={draftQty}
          onChange={(e) => {
            const val = e.target.value;
            if (val === '' || /^\d+$/.test(val)) {
              setDraftQty(val === '' ? '' : Number(val));
            }
          }}
          onBlur={() => commitQuantity(draftQty)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitQuantity(draftQty);
            }
          }}
          className="w-16 sm:w-20 text-center text-xl font-bold border-gray-300 focus:ring-0 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          min="1"
          placeholder="1"
        />

        <button
          type="button"
          onClick={() => setDraftQty(prev => prev + 1)}
          className="w-12 h-12 flex items-center justify-center hover:bg-gray-100 active:bg-gray-200 transition"
        >
          <Plus className="w-5 h-5" />
        </button>
      </div>

      <button
        onClick={() => {
          commitQuantity(draftQty);
          addToCart(product, draftQty);
          setSavingLoader(true);
          setTimeout(() => setSavingLoader(false), 1800);
        }}
        disabled={savingLoader || draftQty < 1}
        style={{
          padding: '1rem 0',
          borderRadius: '0.75rem',
          fontWeight: '700',
          color: 'white',
          minWidth: '180px',
          transition: 'all 0.2s ease',
          backgroundColor: savingLoader ? activeBg : featuredBg,
          opacity: savingLoader || draftQty < 1 ? 0.7 : 1,
          cursor: savingLoader || draftQty < 1 ? 'not-allowed' : 'pointer',
        }}
        className="flex-1 text-center shadow-md hover:shadow-lg active:scale-[0.98] transition-transform"
      >
        {savingLoader ? 'Adding...' : 'Add to Cart'}
      </button>
      </>
      )}
      {product?.qty<1 &&(
      <>
       <button
        onClick={() => {
         alert('Product is out of stock. Check later');
        }}
      
        style={{
          padding: '1rem 0',
          borderRadius: '0.75rem',
          fontWeight: '700',
          color: 'white',
          minWidth: '180px',
          transition: 'all 0.2s ease',
          backgroundColor: savingLoader ? activeBg : featuredBg,
          opacity: savingLoader || draftQty < 1 ? 0.7 : 1,
          cursor: savingLoader || draftQty < 1 ? 'not-allowed' : 'pointer',
        }}
        className="flex-1 text-center shadow-md hover:shadow-lg active:scale-[0.98] transition-transform"
      >
        Out Of Stock
      </button>
      </>
      )}
    </div>
  );
});

const ImageViewer = ({ images, initialIndex = 0, onClose, featuredBg }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);

  const handlePrev = () => { 
    setCurrentIndex(p => (p > 0 ? p - 1 : images.length - 1)); 
    setZoom(1); 
  };

  const handleNext = () => { 
    setCurrentIndex(p => (p < images.length - 1 ? p + 1 : 0)); 
    setZoom(1); 
  };

  useEffect(() => {
    const onKey = e => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === '+' || e.key === '=') setZoom(z => Math.min(z + 0.25, 3));
      if (e.key === '-') setZoom(z => Math.max(z - 0.25, 0.5));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!images?.length) return null;

  return (
    <div 
      className="fixed inset-0 z-[9999999] flex items-center justify-center p-4 bg-black/70"
      onClick={onClose}
    >
      <div 
        className="w-full max-w-2xl rounded-3xl overflow-hidden"
        style={{ 
          background: '#fff', 
          border: '1px solid #e2e0f9', 
          boxShadow: '0 10px 60px rgba(0,0,0,0.3)' 
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div 
          className="flex items-center justify-between px-6 py-4"
          style={{ background: '#fafafe',  }}
        >
          <div className="flex items-center gap-3" >
            <div 
              style={{ 
                width: 10, 
                height: 10, 
                borderRadius: '50%', 
                background: featuredBg 
              }} 
            />
            <span 
              style={{ 
                fontSize: 15, 
                fontWeight: 600, 
                color: featuredBg 
              }}
            >
              {currentIndex + 1} / {images.length}
            </span>
          </div>
         
          <button 
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-2xl hover:bg-red-50 transition-colors"
            style={{ background: '#f1f0fb', color: featuredBg }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Large Image Container - Aspect Square */}
        <div className="relative bg-white flex items-center justify-center p-8" 
             style={{ height: '80vh', background: '#fafafe' }}>
          
          <button 
            onClick={handlePrev}
            className="absolute left-6 w-12 h-12 flex items-center justify-center rounded-2xl z-10 hover:scale-105 transition-transform"
            style={{ 
              background: '#f1f0fb', 
              border: '1px solid #e0e7ff', 
              color: featuredBg 
            }}
          >
            <ChevronLeft size={24} />
          </button>

          <div className="w-full max-w-2xl aspect-square rounded-3xl overflow-hidden">
            <img 
              src={images[currentIndex]} 
              alt={`Image ${currentIndex + 1}`}
              className="w-full h-full object-contain rounded-xl"
              style={{ 
                transform: `scale(${zoom})`, 
                transformOrigin: 'center', 
                transition: 'transform 0.25s ease'
              }} 
            />
          </div>

          <button 
            onClick={handleNext}
            className="absolute right-6 w-12 h-12 flex items-center justify-center rounded-2xl z-10 hover:scale-105 transition-transform"
            style={{ 
              background: '#f1f0fb', 
              border: '1px solid #e0e7ff', 
              color: featuredBg 
            }}
          >
            <ChevronRight size={24} />
          </button>
        </div>

        {/* Footer Controls */}
        <div 
          className="flex items-center justify-between px-6 py-4"
          style={{ background: '#fafafe' }}
        >
          {/* Dots */}
          <div className="flex gap-2 items-center">
            {images.map((_, i) => (
              <div 
                key={i} 
                onClick={() => { 
                  setCurrentIndex(i); 
                  setZoom(1); 
                }}
                className="cursor-pointer rounded-full transition-all hover:scale-110"
                style={{ 
                  width: i === currentIndex ? 28 : 8, 
                  height: 8,
                  background: i === currentIndex ? featuredBg : '#e0e7ff' 
                }} 
              />
            ))}
          </div>

          {/* Zoom Controls */}
          <div 
            className="flex items-center gap-1 px-3 py-1.5 rounded-2xl"
            style={{ 
              background: '#f1f0fb', 
              border: '1px solid #e0e7ff' 
            }}
          >
            <button 
              onClick={() => setZoom(z => Math.max(z - 0.25, 0.5))}
              className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-white/70 transition-colors"
              style={{ color: featuredBg }}
            >
              <Minus size={18} />
            </button>
            
            <span 
              style={{ 
                fontSize: 14, 
                fontWeight: 600, 
                color: featuredBg, 
                minWidth: 52, 
                textAlign: 'center' 
              }}
            >
              {Math.round(zoom * 100)}%
            </span>

            <button 
              onClick={() => setZoom(z => Math.min(z + 0.25, 3))}
              className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-white/70 transition-colors"
              style={{ color: featuredBg }}
            >
              <Plus size={18} />
            </button>
          </div>

          <button 
            onClick={() => setZoom(1)}
            style={{ 
              fontSize: 13, 
              fontWeight: 600, 
              color: featuredBg, 
              background: '#f1f0fb',
              border: '1px solid #e0e7ff', 
              borderRadius: 12, 
              padding: '8px 18px' 
            }}
            className="hover:bg-white/70 transition-colors"
          >
            Reset
          </button>
        </div>
      </div>
    </div>
  );
};



// ── Main Component ───────────────────────────────────────────────────────────
const ProductView = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const token = localStorage.getItem('token');
   let featuredBg = localStorage.getItem('featuredBg') || config.BASE_COLOR;
  const activeBg = localStorage.getItem('activeBg') || config.BASE_COLOR;
if(featuredBg=='bg-gray-400'){
  featuredBg=config.BASE_COLOR;
}

  const {
    addToCart,
    changeQuantity,
    isInCart,
    getQuantity,
  } = useCart();

  const [user, setUser] = useState(null);
  const [product, setProduct] = useState(null);
  const [mainVariants, setMainVariants] = useState([]);
  const [allVariants, setAllVariants] = useState([]);
  const [similarProducts, setSimilarProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImageIdx, setSelectedImageIdx] = useState(0);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [savingLoader, setSavingLoader] = useState(false);
  const [selectedVariants, setSelectedVariants] = useState({});
  const [catNavConfig, setcatNavConfig] = useState(null);
  const [flashConfig, setFlashConfig] = useState(null);
  const [navConfig, setNavConfig] = useState(null);
  const [searchConfig, setSearchConfig] = useState(null);
  const [productViewConfig, setProductViewConfig] = useState(null);
  const [cartConfig, setCartConfig] = useState(null);
  const [footerConfig, setFooterConfig] = useState(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [viewerStartIndex, setViewerStartIndex] = useState(0);
  const [isVariantOpen, setVariantsOpen] = useState(false);

  const detailRef = useRef(null);
  const productRef = useRef(null);
  const [sampleReviews, setSampleReviews] = useState([]);
  const [stats, setStats] = useState([]);

  // Quantity management
  const [draftQty, setDraftQty] = useState(1);
  const cartQty = getQuantity(id) || 0;

  useEffect(() => {
    setDraftQty(cartQty > 0 ? cartQty : 1);
  }, [cartQty, id]);

  const commitQuantity = useCallback((newQty) => {
    const qty = Math.max(1, Number(newQty) || 1);
    changeQuantity(id, qty);
  }, [id, changeQuantity]);

  const openVariants = () => {
    setVariantsOpen(true);
    setTimeout(() => {
      detailRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 50);
  };

  const setWishList = async (status) => {
    try {
      setIsWishlisted(status);
      await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });
      const xsrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];
      const endpoint = '/api/saveWishlist/' + status;

      const response = await axios.post(
        config.API_URL + endpoint,
        { product_id: id, token: token },
        {
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-XSRF-TOKEN": decodeURIComponent(xsrfToken || ''),
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );
    } catch (error) {
      alert("Failed to save.");
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);

  // ── Data Fetching ───────────────────────────────────────────────────────────
  useEffect(() => {
   

    let mounted = true;

    const loadData = async () => {
      const productCacheKey = CACHE_KEY_PRODUCT(id);
      const cachedProduct = getCached(productCacheKey);

      if (cachedProduct?.id && mounted) {
        setProduct(cachedProduct);
        setMainVariants(cachedProduct.mainvariants || []);
        setAllVariants(cachedProduct.variants || []);
        setLoading(false);
      }

      try {
      //  await axios.get(`${config.API_URL}/sanctum/csrf-cookie`, { withCredentials: true });

        const fetchProduct = async () => {
          try {
            const res = await axios.get(`${config.API_URL}/api/getProduct/${id}`, {
              headers: { Authorization: `Bearer ${token}` },
            });

            const { data, mainvariants = [], variants = [] } = res.data;
            if (!data) throw new Error("Product not found");

            const images = (data.images || [])
              .filter(img => img && typeof img === 'string' && img.trim())
              .map(img => `https://admins.junisaproducts.com/images/product/${img}`);

               setTimeout(() => {
      productRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 50);

            const productData = {
              ...data,
              images: images.length > 0 ? images : [''],
              name: data.name || "Unnamed Product",
              description: data.product_details || data.description || "No description available.",
              id: data.id,
              features: data.product_details
                ? data.product_details.match(/<li[^>]*>(.*?)<\/li>/gs)?.map(item => item.replace(/<[^>]+>/g, '').trim()).filter(Boolean) || []
                : ["High-quality material", "Durable stitching", "Comfort fit"],
              rating: Number(data.rating) || 0.0,
              reviews: Number(data.reviews) || 0,
              inStock: (data.qty ?? 0) > 0,
              displayPrice: Number(data.price || data.display_price || 0),
              originalPrice: Number(data?.originalPrice) || null,
              price: Number(data.price) || null,
              mainvariants,
              variants,
            };

            if (mounted) {
              setProduct(productData);
              setStats(res.data.stats);
              setSampleReviews(res.data.Reviews);
              setSimilarProducts(res.data.similarData);
              setMainVariants(mainvariants);
              setAllVariants(variants);
              setLoading(false);
            }

            setCached(productCacheKey, productData);
          } catch (err) {
            console.error("Product fetch failed:", err);
            if (!cachedProduct && mounted) {
              setError("Failed to load product");
              toast.error("Failed to load product");
            }
          }
        };

       /** const fetchLayout = async () => {
          const cachedLayout = getCached(CACHE_KEY_LAYOUT);
          if (cachedLayout && mounted) {
            setProductViewConfig(cachedLayout.productView || null);
          }

          try {
            const [pvRes] = await Promise.all([
              axios.get(`${config.API_URL}/api/pullProductViewSettings`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} })),
            ]);

            const layoutData = {
              productView: pvRes.data.productViewSettings || null,
            };

            if (mounted) {
              setProductViewConfig(layoutData.productView);
            }

            setCached(CACHE_KEY_LAYOUT, layoutData);
          } catch (err) {
            console.error("Layout fetch failed:", err);
          }
        };**/

        await Promise.all([fetchProduct()]);
      } catch (err) {
        console.error(err);
        if (mounted && !cachedProduct) {
          setError(err.response?.data?.message || "Failed to load data");
          toast.error("Failed to load data");
        }
      }
    };

    loadData();

    return () => { mounted = false; };
  }, [id]);

  useEffect(() => {
    if (allVariants.length > 0) {
      // setHasVariant(true); // Uncomment if still using this state
    }
  }, [allVariants]);

  const productDefault = { activeDesign: 'design2', devMode: false, customStyles: {} };

  // ── Variant Logic ───────────────────────────────────────────────────────────
  const colorMain = useMemo(() =>
    mainVariants.find(m => allVariants.some(v => v.variant_id === m.id && v.item_code?.trim())) || mainVariants[0],
  [mainVariants, allVariants]);

  const colorSubs = useMemo(() =>
    allVariants.filter(v => v.variant_id === colorMain?.id && v.item_code?.trim()),
  [allVariants, colorMain?.id]);

  const hasVariants = colorSubs.length > 0;

  const selectedCount = useMemo(() =>
    Object.values(selectedVariants[colorMain?.id || {}] || {}).reduce((sum, q) => sum + q, 0),
  [selectedVariants, colorMain?.id]);

  const toggleSubVariant = useCallback((subId) => {
    const mainId = colorMain?.id;
    if (!mainId) return;
    setSelectedVariants(prev => {
      const group = { ...prev[mainId] || {} };
      if (group[subId]) {
        const { [subId]: removed, ...rest } = group;
        if (Object.keys(rest).length === 0) {
          const { [mainId]: _, ...newState } = prev;
          return newState;
        }
        return { ...prev, [mainId]: rest };
      }
      return { ...prev, [mainId]: { ...group, [subId]: 1 } };
    });
  }, [colorMain?.id]);

  // ── Render ──────────────────────────────────────────────────────────────────
  if (error && !product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6 text-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Product Not Found</h2>
          <p className="text-gray-600 mb-8">{error}</p>
          <a onClick={(e)=>navigate('/Shop/all')} className="inline-block cursor-pointer bg-gray-900 text-white px-8 py-4 rounded-xl font-semibold hover:bg-gray-800">
            ← Back to Shopping
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <ShareableNav />

      {loading && !product ? (
        <ProductSkeleton />
      ) : (
        <main ref={productRef} className="flex-grow">
          <br /><br /><br /><br /><br />
<br /><br />


          <div  className="product-detail-container md-[mt-60px] max-w-7xl mx-auto px-4 sm:px-6 py-8 md:py-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">

              {/* LEFT: Product Info */}
              <div className="order-2 lg:order-1 stagger-fade-in">
                <h1 className="product-title text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                  {product?.name}
                </h1>

                <div className="section-divider"></div>

                <div className="price-section flex items-center gap-4 mb-8">
                  <span className="price-badge text-3xl md:text-4xl font-bold text-gray-900">
                    KES {product?.displayPrice ?? '0'}
                  </span>
                  {product?.originalPrice && product.displayPrice < product.originalPrice && (
                    <span className="text-xl md:text-2xl text-gray-400 line-through">
                      KES {product.originalPrice}
                    </span>
                  )}
                </div>

                <div className="content-card description-section mb-8">
                  <div
                    className="text-gray-700 text-base md:text-lg leading-relaxed prose prose-slate max-w-none"
                    dangerouslySetInnerHTML={{ __html: product?.description }}
                  />
                </div>

                <QuantityAndAddButton
                  allVariants={allVariants}
                  openVariants={openVariants}
                  featuredBg={featuredBg}
                  activeBg={activeBg}
                  draftQty={draftQty}
                  setDraftQty={setDraftQty}
                  commitQuantity={commitQuantity}
                  addToCart={addToCart}
                  product={product}
                  savingLoader={savingLoader}
                  setSavingLoader={setSavingLoader}
                />
              </div>

              {/* RIGHT: Images + thumbnails */}
              <div className="space-y-6 order-1 lg:order-2">
                <div className="flex flex-col-reverse md:flex-col-reverse md:flex-row gap-4">

                <style>{`
/* Horizontal scrollbar styling */
.thumbnail-grid::-webkit-scrollbar {
  height: 6px;
}

.thumbnail-grid::-webkit-scrollbar-track {
  background: gray !important;
}

.thumbnail-grid::-webkit-scrollbar-thumb {
  background-color: black !important; /* soft gray */
  border-radius: 10px;
}

.thumbnail-grid::-webkit-scrollbar-thumb:hover {
  background-color: #94a3b8;
}

/* Firefox */
.thumbnail-grid {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
}
`}</style>
                <div className="w-full thumbnail-grid relative">
  <div
    className="flex gap-3 overflow-x-auto flex-nowrap 
               px-2 pb-2 pr-6
               snap-x snap-mandatory"
  >
    {product?.images?.map((img, idx) => (
      <button
        key={idx}
        onClick={() => setSelectedImageIdx(idx)}
        className={`flex-shrink-0 snap-start rounded-xl overflow-hidden bg-gray-100 border-2 transition-all
          ${selectedImageIdx === idx
            ? 'border-blue-500 shadow-md scale-105'
            : 'border-gray-200 hover:border-blue-300'
          }`}
      >
        <img
          onClick={() => {
            setViewerStartIndex(idx);
            setIsViewerOpen(true);
          }}
          src={img}
          alt={`Thumbnail ${idx + 1}`}
          className="w-24 h-24 md:w-28 md:h-28 object-cover"
          loading="lazy"
        />
      </button>
    ))}
  </div>

  {/* Right gradient hint */}
  <div className=" absolute right-0 top-0 h-full w-20 bg-gradient-to-l from-orange to-black" />
</div>

                  <div className="md:w-[100%] mt-[35px] md:mt-[0px] main-image-container">
                    <div className="main-image-wrapper aspect-[4/3] md:aspect-[5/6] rounded-2xl overflow-hidden shadow-2xl relative bg-gradient-to-br from-gray-100 to-gray-50">
                      
<style>{`
  /* ── E-commerce product image animations ── */
  .main-product-image {
    transform-origin: center center;
  }

  @keyframes product-breathe {
    0%, 100% {
      transform: scale(1);
      filter: brightness(1) saturate(1);
    }
    40% {
      transform: scale(1.012);
      filter: brightness(1.04) saturate(1.08);
    }
    70% {
      transform: scale(1.008);
      filter: brightness(1.02) saturate(1.05);
    }
  }

  @keyframes product-shine {
    0%   { --shine-x: -60%; opacity: 0; }
    10%  { opacity: 1; }
    90%  { opacity: 1; }
    100% { --shine-x: 160%; opacity: 0; }
  }

  @keyframes product-zoom-in {
    0%   { transform: scale(1.05); }
    60%  { transform: scale(1.075); }
    80%  { transform: scale(1.068); }
    100% { transform: scale(1.07); }
  }

  .main-product-image {
    animation: product-breathe 5s ease-in-out infinite;
    transition: filter 0.3s ease, transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
    will-change: transform, filter;
  }

  .main-product-image:hover {
    animation: product-zoom-in 0.75s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
    filter: brightness(1.06) saturate(1.12) contrast(1.02);
  }
`}</style>
                      <img
  onClick={() => { setViewerStartIndex(selectedImageIdx); setIsViewerOpen(true); }}
  src={product?.images?.[selectedImageIdx] || "https://placehold.co/loading"}
  alt={product?.name}
  loading="lazy"
  onError={e => e.target.src = "https://placehold.co/600x750?text=Image+Error"}
  className="
    main-product-image
    w-full h-full object-cover cursor-pointer
    
  "
/>

                      <button
                        onClick={() => setIsWishlisted(!isWishlisted)}
                        className="wishlist-button absolute top-5 right-5 w-14 h-14 bg-white/95 rounded-full flex items-center justify-center shadow-xl hover:shadow-2xl z-20 border border-gray-100"
                      >
                        <Heart
                          className={`w-6 h-6 transition-colors duration-300 ${isWishlisted ? 'fill-red-500 text-red-500' : 'text-gray-700'}`}
                        />
                      </button>

                      {product?.originalPrice && product?.displayPrice < product?.originalPrice && (
                        <div className="discount-badge absolute top-5 left-5 bg-gradient-to-r from-red-500 to-pink-500 text-white px-5 py-2.5 rounded-full font-bold text-sm shadow-xl z-20">
                          <span className="relative z-10">
                            Save {Math.round(((product.originalPrice - product.displayPrice) / product.originalPrice) * 100)}%
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>


            {allVariants.length > 0 && product.qty>0 && (
              <div ref={detailRef}>
              <br/>
                <BulkTable product={product} allVariants={allVariants} featuredBg={featuredBg} />
              </div>
            )}

            <div className="mt-16 animate-fadeIn">
              <ReviewsSection
                rating={product?.rating}
                reviewCount={product?.reviews}
                stats={stats}
                sampleReviews={sampleReviews}
                navigate={navigate}
                id={id}
                featuredBg={featuredBg}
              />
            </div>

            <div className="mt-4 animate-fadeIn">
              <Suggestions />
            </div>
          </div>
        </main>
      )}

      {isViewerOpen && product?.images && (
        <ImageViewer
          images={product.images}
          initialIndex={selectedImageIdx}
          onClose={() => setIsViewerOpen(false)}
          featuredBg={featuredBg}
        />
      )}
    </div>
  );
};

export default ProductView;